import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedStat — counts up to `target` once it enters the viewport.
 * Supports decimals, suffixes, and a 0..1 progress signal for the gold bar.
 *
 * Props:
 *   - value: string (the target number, e.g. "40" or "100")
 *   - suffix: string (e.g. "+", "%", ":1")
 *   - label: string
 *   - duration: ms (default 1800)
 *   - delay: ms (default 0)
 */
export default function AnimatedStat({ value, suffix = '', label, duration = 1800, delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(0);
  const [progress, setProgress] = useState(0);
  const [started, setStarted] = useState(false);

  // Determine the numeric target. If value is non-numeric (e.g. "\u221e"), skip the count-up.
  const target = parseFloat(String(value).replace(/[^\d.\-]/g, ''));
  const isNumeric = !Number.isNaN(target);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          setStarted(true);
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let raf;
    let startTime;
    const startDelay = setTimeout(() => {
      const tick = (t) => {
        if (!startTime) startTime = t;
        const elapsed = t - startTime;
        const p = Math.min(1, elapsed / duration);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - p, 3);
        setProgress(eased);
        if (isNumeric) setShown(target * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
        else { setProgress(1); if (isNumeric) setShown(target); }
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(startDelay); if (raf) cancelAnimationFrame(raf); };
  }, [started, duration, delay, target, isNumeric]);

  const display = isNumeric
    ? (Number.isInteger(target) ? Math.floor(shown).toString() : shown.toFixed(1))
    : value;

  return (
    <div ref={ref} className="relative pl-4 md:pl-5 group" data-testid={`stat-${label}`}>
      {/* Gold animated line that grows up */}
      <span
        className="absolute left-0 bottom-0 w-[2px] bg-[color:var(--kc-gold)] origin-bottom transition-[height] duration-[1200ms] ease-out"
        style={{ height: `${progress * 100}%` }}
      />
      <div className="relative">
        <div className="font-display text-5xl md:text-6xl leading-none text-[color:var(--kc-gold)] kc-stat-glow tabular-nums" data-testid="stat-value">
          {display}<span className="text-[color:var(--kc-gold)]">{suffix}</span>
        </div>
        <div className="mt-2 text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/65" data-testid="stat-label">{label}</div>
      </div>
      <style>{`
        .kc-stat-glow { text-shadow: 0 0 26px rgba(212,164,55,0.35), 0 0 60px rgba(212,164,55,0.15); }
      `}</style>
    </div>
  );
}
